import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function RemodelDeckCalgaryBlog() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Only intercept internal links
    if (href.startsWith('/') || href.includes('busybeaverconstruction.com/blog') || href.includes('busybeaverconstruction.com/contact') || href.includes('busybeaverconstruction.com/estimate')) {
      e.preventDefault()
      const path = href.replace(/^https?:\/\/[^\/]+/, '')
      window.history.pushState({}, '', path)
      window.dispatchEvent(new Event('popstate'))
      window.scrollTo(0, 0)
    }
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Consolidated Schema Data for Remodel Deck Calgary Blog
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": "How to Remodel a Deck in Calgary",
        "description": "A complete guide to remodeling an existing deck in Calgary, including structural assessments, costs, permits, and comparing wood vs. composite decking.",
        "image": "https://busybeaverconstruction.com/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
        "author": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "url": "https://busybeaverconstruction.com/"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "logo": {
            "@type": "ImageObject",
            "url": "https://busybeaverconstruction.com/assets/images/logo.png"
          }
        },
        "datePublished": "2026-08-10T00:00:00+00:00",
        "dateModified": "2026-08-10T00:00:00+00:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://busybeaverconstruction.com/blog/remodel-deck-calgary"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "How to Remodel a Deck in Calgary",
            "item": "https://busybeaverconstruction.com/blog/remodel-deck-calgary"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Can You Remodel an Existing Deck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, an existing deck can often be remodeled when the underlying structure is still in suitable condition. You may be able to replace the decking, railings, stairs, or other components without rebuilding the entire deck. The framing, posts, beams, connections, and footings should be assessed before deciding what can be retained."
            }
          },
          {
            "@type": "Question",
            "name": "How Do I Know If My Deck Frame Is Still Good?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Look for signs such as rot, soft wood, sagging, movement, splitting, damaged connections, or deterioration around posts, beams, and the ledger area. A visual check can identify obvious problems, but structural concerns should be assessed by someone qualified before remodeling work begins."
            }
          },
          {
            "@type": "Question",
            "name": "Can I Put Composite Decking Over an Existing Deck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Not automatically. The existing framing must be suitable for the specific composite product, including its joist spacing, fastening requirements, ventilation, and installation pattern. The manufacturer's installation instructions should be followed before installing composite decking over an existing structure."
            }
          },
          {
            "@type": "Question",
            "name": "How Much Does It Cost to Remodel a Deck in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "There is no single price because the cost depends on the size and condition of the deck, materials, structural repairs, railings, stairs, access, and overall scope. Calgary market benchmarks can provide a starting point, but an assessment is needed for a project specific estimate."
            }
          },
          {
            "@type": "Question",
            "name": "Do I Need a Permit to Remodel My Deck in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It depends on the deck and the work being completed. An elevated uncovered deck over 0.6 metres above grade can require a building permit, including certain structural work, extensions, and stair work. Always check the current City of Calgary requirements before starting the project."
            }
          },
          {
            "@type": "Question",
            "name": "Is It Cheaper to Remodel or Replace a Deck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Remodeling can cost less when the existing structure is sound and only selected components need to be replaced. If the framing or foundation has widespread deterioration, repeated repairs may not be practical and a complete replacement can make more sense."
            }
          },
          {
            "@type": "Question",
            "name": "How Long Does a Deck Remodel Take?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The timeline depends on the size of the deck, its existing condition, the amount of structural work, materials, design changes, and permitting requirements. A straightforward resurfacing project will generally take less time than a remodel involving structural repairs or a larger layout change."
            }
          }
        ]
      }
    ]
  };

  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>How to Remodel a Deck in Calgary | Busy Beaver Construction</title>
        <meta name="description" content="A complete guide to remodeling an existing deck in Calgary, including structural assessments, costs, permits, and comparing wood vs. composite decking." />
        <meta name="keywords" content="Calgary deck remodel, deck remodeling Calgary, repair vs replace deck, composite decking Calgary, deck framing Calgary" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/remodel-deck-calgary" />
      </Helmet>
      
      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[450px] md:h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img 
          src="/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg"
          alt="Calgary deck remodeling and structural assessment"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-24 text-center max-w-5xl mx-auto px-4">
          <div className="inline-block bg-[#86efac] rounded py-1 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Remodeling Guide</p>
          </div>
          <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight italic">
            How to Remodel a Deck in Calgary
          </h1>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 relative">
            
            <article className="lg:col-span-8 space-y-6 text-[17px] text-gray-700 leading-relaxed font-text">
              
              <div className="article-introduction">
                <p className="mb-6">
                  An older deck does not always need to be completely rebuilt. In Calgary, weathered decking, worn railings, or an
                  outdated design can often be addressed through a well planned remodel, provided the underlying structure is in good
                  condition.
                </p>
                <p className="mb-6">
                  If you're considering hiring a{' '}
                  <a 
                    href="https://busybeaverconstruction.com" 
                    className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors"
                  >
                    Calgary deck builder
                  </a>
                  , the first step is to determine what can be retained and what needs to be repaired or replaced. From the deck
                  framing and footings to the decking material, railings, stairs, and layout, each part can affect the scope and cost
                  of the project.
                </p>
                <p className="mb-6">
                  This guide explains how to remodel a deck in Calgary, including how to assess an existing structure, choose between
                  wood and composite decking, understand potential costs and permit considerations, and decide when remodeling makes
                  more sense than a complete replacement.
                </p>
              </div>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Can You Remodel an Existing Deck?</h2>
              <p className="mb-6">
                Yes, you can remodel an existing deck if the main structure is still in good condition. In Calgary, an older deck
                may have weathered boards, faded finishes, or outdated railings while the framing underneath remains usable. The
                important step is to assess the structure before deciding what to replace.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">When Remodeling Makes Sense</h3>
              <p className="mb-4">
                Remodeling can make sense when the existing framing, posts, beams, and footings are generally sound. You may be able
                to replace the decking, upgrade the railings and stairs, improve the layout, or switch from wood to composite
                decking without rebuilding everything.
              </p>
              <p className="mb-6">
                This can be a practical option for Calgary homeowners who like their existing deck location but want to improve its
                appearance, functionality, or maintenance requirements. Before choosing a new surface, it is also worth comparing{' '}
                <a 
                  href="https://busybeaverconstruction.com/blog/best-decking-material-calgary-winters" 
                  onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/best-decking-material-calgary-winters')}
                  className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors"
                >
                  decking materials suited to Calgary winters
                </a>
                .
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">When Replacement Is the Better Option</h3>
              <p className="mb-4">
                A complete replacement may be more appropriate when the existing structure has widespread rot, significant movement,
                damaged framing, unstable posts, or problems with the foundation. Major changes to the deck size or layout may also
                require more extensive reconstruction.
              </p>
              <p className="mb-6">
                Replacing the surface without addressing serious structural problems can leave the underlying issue unresolved. If
                you are unsure which option is right for your deck, compare{' '}
                <a 
                  href="https://busybeaverconstruction.com/blog/deck-replacement-vs-repair-calgary" 
                  onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/deck-replacement-vs-repair-calgary')}
                  className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors"
                >
                  deck replacement vs. repair in Calgary
                </a>
                {' '}before making a decision.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Cosmetic vs. Structural Problems</h3>
              <p className="mb-4">
                Cosmetic problems include faded decking, cracked boards, worn railings, peeling finishes, and an outdated
                appearance. These can often be addressed through remodeling or resurfacing.
              </p>
              <p className="mb-6">
                Structural problems include rotten framing, sagging joists, unstable posts, damaged beams, or movement around the
                footings. These require closer assessment before any remodeling work begins.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Remodel or Replace?</h3>
              <div className="overflow-x-auto my-8 shadow-sm rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-4 font-bold font-secondary">Your deck condition</th>
                      <th className="p-4 font-bold font-secondary">Usually consider</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Worn boards, sound frame</td>
                      <td className="p-4 text-gray-700">Remodel or resurface</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Outdated railings or stairs</td>
                      <td className="p-4 text-gray-700">Remodel</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Minor structural repairs needed</td>
                      <td className="p-4 text-gray-700">Remodel with repairs</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Widespread structural deterioration</td>
                      <td className="p-4 text-gray-700">Replacement</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Major layout or size changes</td>
                      <td className="p-4 text-gray-700">Assessment before deciding</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mb-6">
                The right choice depends on what is underneath the visible decking. A professional assessment can help determine how
                much of the existing deck can realistically be retained.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">How to Tell If Your Deck Can Be Remodeled</h2>
              <p className="mb-4">
                Before replacing the decking or changing the design, look at what is underneath it. In Calgary, an older deck can
                show surface wear from weather and still have a usable structure. The opposite can also happen: the boards may look
                acceptable while the framing underneath has deterioration that needs attention.
              </p>
              <p className="mb-6">A proper assessment should look at the entire deck, not just the boards you can see.</p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Check the Deck Boards</h3>
              <p className="mb-4">
                Start with the most visible part of the deck. Look for soft or rotten areas, deep cracks, splitting, cupping, and
                boards that have become badly warped.
              </p>
              <p className="mb-4">
                Surface damage does not automatically mean the entire deck needs to be replaced. If the underlying structure is
                sound, replacing the decking can be part of a larger remodel.
              </p>
              <p className="mb-6">
                Pay particular attention to areas that stay damp or receive frequent water exposure. Calgary's changing weather can
                contribute to moisture-related deterioration over time.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Inspect the Joists and Framing</h3>
              <p className="mb-4">The framing determines whether the existing deck can safely support a new surface.</p>
              <p className="mb-2">Check the joists for:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>Soft or rotten sections</li>
                <li>Sagging or noticeable movement</li>
                <li>Splitting or deterioration</li>
                <li>Signs of prolonged moisture exposure</li>
                <li>Spacing that is suitable for the proposed decking</li>
              </ul>
              <p className="mb-4">
                Joist spacing is particularly important if you are changing from wood to composite decking. The requirements can
                vary by product and installation pattern, so the manufacturer's installation instructions should be checked before
                deciding that an existing frame can be reused.
              </p>
              <p className="mb-6">
                If structural components need to be replaced or rebuilt, Calgary may require a building permit depending on the deck
                and the work involved. The City of Calgary specifically includes replacement of structural components among work
                that can require a permit for an uncovered deck over 0.6 metres above grade.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Check Posts, Beams and Connections</h3>
              <p className="mb-4">
                Look underneath the deck at the posts, beams, and their connections. Check for movement, deterioration, splitting,
                corrosion, or connections that no longer appear secure.
              </p>
              <p className="mb-6">
                Do not assume that adding new decking will solve movement in the structure. If the support system is unstable, the
                underlying problem needs to be addressed first.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Inspect the Ledger Board and Flashing</h3>
              <p className="mb-4">
                For a deck attached to the house, the ledger connection deserves particular attention. Look for signs of water
                damage, rot, separation, or deterioration around the connection between the deck and the house.
              </p>
              <p className="mb-6">
                Flashing is also important because it helps protect the building from water entering around the ledger area. If this
                area shows signs of damage, it should be assessed before the existing deck is reused.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Check the Footings</h3>
              <p className="mb-4">
                Finally, look at the deck's support points. Signs such as settlement, movement, leaning posts, or noticeable changes
                in the deck's level can indicate a problem with the foundation or support system.
              </p>
              <p className="mb-4">
                For Calgary projects, foundation details are part of the City's deck permit documentation, including items such as
                foundation depth and diameter, post size, beam size, and joist information.
              </p>
              <p className="mb-6">
                If you cannot confidently assess these structural components, this is the point where a professional deck assessment
                becomes worthwhile. It is better to determine the condition of the structure before spending money on new decking,
                railings, or other upgrades.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">How to Remodel a Deck: Step-by-Step</h2>
              <p className="mb-6">
                Remodeling a deck is more than removing old boards and installing new ones. In Calgary, the condition of the
                existing structure should guide the entire project. A deck that has weathered through several Alberta winters may
                need some framing repairs before new decking can be installed.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">1. Inspect the Existing Structure
              </h3>
              <p className="mb-4">
                Start by checking the decking, joists, beams, posts, connections, ledger area, and footings. Look for rot, movement,
                splitting, sagging, water damage, and other signs that the structure needs attention.
              </p>
              <p className="mb-6">If there are significant structural concerns, resolve those before moving on to cosmetic improvements.</p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">2. Decide What Can Be Retained</h3>
              <p className="mb-4">Once the structure has been assessed, determine which components can reasonably remain.</p>
              <p className="mb-6">
                Depending on the condition of the deck, you may be able to retain some of the existing framing while replacing the
                decking, railings, stairs, or other components. The proposed new decking also needs to be compatible with the
                existing framing and its spacing.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">3. Remove Damaged Decking</h3>
              <p className="mb-4">
                Remove boards that are damaged or no longer suitable for use. Work carefully around the framing so that additional
                damage is not caused during removal.
              </p>
              <p className="mb-6">This is also a useful stage for taking a closer look at areas that were hidden beneath the old decking.</p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">4. Repair or Reinforce the Framing</h3>
              <p className="mb-4">
                If inspection identifies localized deterioration, address it before installing the new surface. Depending on the
                condition, this may involve replacing damaged framing or making other structural corrections.
              </p>
              <p className="mb-6">
                Structural work should be carried out appropriately for the project and in accordance with applicable Alberta
                requirements.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">5. Prepare the Structure</h3>
              <p className="mb-4">
                Before installing new decking, make sure the framing is properly prepared. Check the layout, spacing, level,
                drainage, and condition of the supporting structure.
              </p>
              <p className="mb-6">
                If you are changing decking materials, follow the specific installation requirements provided by the manufacturer.
                Composite decking, for example, may have different framing requirements depending on the product and installation
                pattern.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">6. Install New Decking</h3>
              <p className="mb-4">
                Choose decking based on how you plan to use the space, your maintenance expectations, appearance preferences, and
                Calgary's climate.
              </p>
              <p className="mb-6">
                Install the boards according to the manufacturer's requirements, paying attention to spacing, fastening,
                ventilation, and drainage.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">7. Upgrade Railings and Stairs</h3>
              <p className="mb-4">
                A deck remodel is a good opportunity to replace worn railings or improve the stairs. These elements affect both the
                appearance and safe use of the deck.
              </p>
              <p className="mb-6">
                For elevated decks, railing and stair requirements should be checked against current Calgary and Alberta
                requirements rather than relying on general online advice.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">8. Add Features That Improve the Space</h3>
              <p className="mb-4">
                Once the main structure is complete, consider features that make the deck more useful. Depending on the property,
                this could include privacy walls, lighting, built-in seating, or a pergola.
              </p>
              <p className="mb-6">
                The best additions should fit the existing home, yard, and intended use of the deck rather than simply adding
                features for appearance.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">9. Complete the Finishing and Maintenance</h3>
              <p className="mb-4">
                Finish the project by checking the decking, connections, stairs, railings, and surrounding area. For wood decks,
                establish a maintenance routine appropriate for the material and Calgary's weather.
              </p>
              <p className="mb-6">
                For a major remodel, keeping documentation about the materials and work completed can also be useful for future
                maintenance and property records.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Deck Remodeling Ideas for Calgary Homes</h2>
              <p className="mb-6">
                A deck remodel does not always require a completely new design. In Calgary, many homeowners can make a noticeable
                improvement by updating the parts of the deck that are worn, outdated, or no longer suit how they use their outdoor
                space.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Replace Old Decking</h3>
              <p className="mb-6">
                If the framing is still in good condition, replacing worn deck boards can give the entire space a fresh look. This
                is often one of the most noticeable changes in a deck remodel.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Upgrade to Composite</h3>
              <p className="mb-6">
                Composite decking can be considered when you want a lower maintenance surface and a different appearance from
                traditional wood. The right product should be selected based on its installation requirements and performance in
                Calgary's climate.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Replace Railings</h3>
              <p className="mb-6">
                New railings can change the appearance of an older deck while also improving its usability. Options can include
                wood, aluminum, glass, or other systems suitable for the design.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Add Privacy Screens</h3>
              <p className="mb-6">
                Privacy walls or screens can make a deck more comfortable, particularly for Calgary homes with close neighbouring
                properties. They can also help define separate areas for dining or relaxing.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Improve the Stairs</h3>
              <p className="mb-6">
                Replacing worn stairs or changing their layout can improve access to the yard and make the deck feel more connected
                to the outdoor space.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Expand the Deck</h3>
              <p className="mb-6">
                If the existing deck feels too small, an extension may provide more room for dining, entertaining, or outdoor
                furniture. The existing structure and site conditions should be assessed before expanding.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Add Lighting</h3>
              <p className="mb-6">
                Deck lighting can improve visibility around stairs and walkways while making the space more useful in the evening.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Add a Pergola or Built-In Seating</h3>
              <p className="mb-6">
                A pergola can provide definition and overhead structure, while built-in seating can make better use of limited deck
                space. Both should be planned around the existing deck structure and the way the family uses the space.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Wood vs. Composite for a Deck Remodel</h2>
              <p className="mb-6">
                Choosing between wood and composite decking comes down to budget, maintenance preferences, appearance, and how you
                want the deck to perform through Calgary's changing seasons.
              </p>

              <div className="overflow-x-auto my-8 shadow-sm rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-4 font-bold font-secondary">Factor</th>
                      <th className="p-4 font-bold font-secondary">Wood Decking</th>
                      <th className="p-4 font-bold font-secondary">Composite Decking</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-bold">Cost</td>
                      <td className="p-4 text-gray-700">Usually has a lower upfront material cost</td>
                      <td className="p-4 text-gray-700">Generally costs more initially</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-bold">Maintenance</td>
                      <td className="p-4 text-gray-700">Requires regular cleaning, staining, and upkeep</td>
                      <td className="p-4 text-gray-700">Requires less routine maintenance</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-bold">Durability</td>
                      <td className="p-4 text-gray-700">Can perform well with proper maintenance</td>
                      <td className="p-4 text-gray-700">Designed for long term outdoor use, with performance varying by product</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-bold">Appearance</td>
                      <td className="p-4 text-gray-700">Natural wood appearance with different stain options</td>
                      <td className="p-4 text-gray-700">Available in several colours and wood grain finishes</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-bold">Calgary weather</td>
                      <td className="p-4 text-gray-700">Needs protection from moisture, snow, and seasonal weather changes</td>
                      <td className="p-4 text-gray-700">A suitable product can be a lower maintenance option for Calgary's climate</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-bold">Best suited for</td>
                      <td className="p-4 text-gray-700">Homeowners who prefer natural wood and are comfortable with maintenance</td>
                      <td className="p-4 text-gray-700">Homeowners who prefer lower maintenance and a more consistent appearance</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mb-4">
                For a Calgary deck remodel, there is no single material that is right for every homeowner. Wood may suit someone who
                values the natural look and lower initial cost, while composite may suit someone who wants to spend less time
                maintaining the deck.
              </p>
              <p className="mb-6">
                The final choice should also consider the existing framing. Different decking products have specific requirements
                for joist spacing, fastening, ventilation, and installation. Check the manufacturer's instructions before reusing an
                existing structure.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">How Much Does It Cost to Remodel a Deck in Calgary?</h2>
              <p className="mb-4">
                The cost to remodel a deck in Calgary depends on what can be kept and what needs to be repaired or replaced.
                Replacing worn decking is very different from rebuilding the framing, changing the layout, or replacing the entire
                deck.
              </p>
              <p className="mb-4">
                Local pricing can provide a useful starting point, but every deck is different. The condition of the existing
                structure, material selection, deck size, access, and project scope can all change the final cost.
              </p>

              <div className="bg-amber-50 p-6 md:p-8 rounded-lg border-l-4 border-amber-500 my-8 shadow-sm">
                <p className="text-gray-900 font-medium m-0 leading-relaxed">
                  Cost Disclaimer: The prices below are general Calgary market benchmarks identified during our research. They are
                  provided for planning purposes only and should not be treated as fixed Busy Beaver pricing or a project quote.
                  Actual costs can vary based on the existing deck's condition, materials, labour, site conditions, design, and scope
                  of work. A project specific assessment is recommended for an accurate estimate.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Typical Calgary Deck Remodeling Costs</h3>
              <div className="overflow-x-auto my-8 shadow-sm rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-4 font-bold font-secondary">Remodeling work</th>
                      <th className="p-4 font-bold font-secondary">Indicative Calgary benchmark</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Wood deck resurfacing</td>
                      <td className="p-4 text-gray-700 font-bold text-beaver-green">$15 to $30+ per sq. ft.</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Composite deck resurfacing</td>
                      <td className="p-4 text-gray-700 font-bold text-beaver-green">$30 to $50+ per sq. ft.</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Full wood deck replacement</td>
                      <td className="p-4 text-gray-700 font-bold text-beaver-green">$30 to $70 per sq. ft.</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Full composite deck replacement</td>
                      <td className="p-4 text-gray-700 font-bold text-beaver-green">$44.50+ per sq. ft.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mb-6">
                These figures come from the Calgary pricing benchmarks identified in the research and should be independently
                checked for current pricing before publication.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">What Affects the Final Cost?</h3>
              <ul className="list-disc pl-6 space-y-3 mb-6 marker:text-[#86efac]">
                <li><p className="m-0">Structural repairs: Damaged joists, beams, posts, or other framing can increase the scope considerably.</p></li>
                <li><p className="m-0">Wood or composite: Composite generally requires a higher initial investment than wood, while the maintenance requirements can differ over time.</p></li>
                <li><p className="m-0">Railings and stairs: Replacing or redesigning these components adds material and labour costs.</p></li>
                <li><p className="m-0">Deck size and layout: A larger deck requires more materials and labour. Expanding the existing footprint can also introduce additional structural and permitting considerations.</p></li>
                <li><p className="m-0">Site conditions: Backyard access, demolition, disposal, existing framing, and the condition of the supporting structure can all affect the final price.</p></li>
                <li><p className="m-0">For that reason, an online price range should be used to understand the potential scale of a project, not as a substitute for a project specific estimate.</p></li>
              </ul>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Do You Need a Permit to Remodel a Deck in Calgary?</h2>
              <p className="mb-6">
                Not every deck remodel requires a new permit in Calgary. The requirement depends on the height of the deck and the
                type of work being done. According to the City of Calgary, a building permit is required when an uncovered deck is
                higher than 0.6 metres (2 feet) above grade at any point and you are rebuilding, extending, replacing structural
                components, or adding stairs. Covered decks have additional requirements.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">When Might a Remodel Need a Permit?</h3>
              <p className="mb-4">
                If you are only replacing surface boards on a low, uncovered deck, the work may not require a building permit.
                Structural changes are different. Replacing posts, beams, joists, or other structural components on an elevated deck
                can require a permit. Adding stairs or extending the deck can also trigger permitting requirements.
              </p>
              <p className="mb-4">
                You also need to consider setbacks and Calgary land use rules. A project can require additional approval depending
                on where the deck sits on the property.
              </p>
              <p className="mb-4">
                If you are adding a pergola or roof, check the requirements before construction because the structure may fall under
                different permit rules.
              </p>
              <p className="mb-4">
                For a more detailed explanation of Calgary deck permits, see our guide on{' '}
                <a 
                  href="https://busybeaverconstruction.com/blog/4" 
                  onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/4')}
                  className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors"
                >
                  whether you need a permit to build a deck in Calgary
                </a>
                .
              </p>
              <p className="mb-6">
                For the most current requirements, always check the{' '}
                <a 
                  href="https://www.calgary.ca/development/home-building/decks.html" 
                  className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  City of Calgary's official deck permit requirements
                </a>
                {' '}before starting work.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Remodel or Replace Your Deck: Which Is Right for You?</h2>
              <p className="mb-6">
                The right choice depends on the condition of the existing structure and what you want the finished deck to do. In
                Calgary, a deck that has worn boards or outdated railings may only need targeted improvements, while widespread
                structural deterioration can make a complete replacement more practical.
              </p>

              <div className="overflow-x-auto my-8 shadow-sm rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-4 font-bold font-secondary">Remodel or Resurface</th>
                      <th className="p-4 font-bold font-secondary">Replace</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-700">Structure is generally sound</td>
                      <td className="p-4 text-gray-700">Major structural deterioration</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-700">Mainly surface or design upgrades</td>
                      <td className="p-4 text-gray-700">New framing or major structural work</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-700">Existing layout still works</td>
                      <td className="p-4 text-gray-700">Want a significantly different layout or size</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-700">Smaller project scope</td>
                      <td className="p-4 text-gray-700">Major reconstruction is required</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-700">Existing components can be retained</td>
                      <td className="p-4 text-gray-700">Much of the existing structure needs replacement</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mb-4">
                If the framing is sound, remodeling can allow you to update the decking, railings, stairs, or layout without
                replacing every component. If major structural elements are deteriorated, rebuilding may provide a better long term
                solution than repeatedly repairing individual sections.
              </p>
              <p className="mb-6">
                The decision should be based on an assessment of the existing deck rather than appearance alone. If you are still
                deciding between the two options, our guide to{' '}
                <a 
                  href="https://busybeaverconstruction.com/blog/deck-replacement-vs-repair-calgary" 
                  onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/deck-replacement-vs-repair-calgary')}
                  className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors"
                >
                  deck replacement vs. repair in Calgary
                </a>
                {' '}provides more detail on the factors to consider.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Should You Remodel a Deck Yourself or Hire a Professional?</h2>
              <p className="mb-6">
                Some parts of a deck remodel can be handled by homeowners, but structural work requires more care. In Calgary, the
                right approach depends on the condition of the existing deck, the scope of the project, and whether permits or
                structural changes are involved.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">What Can Homeowners Reasonably DIY?</h3>
              <p className="mb-6">
                Cleaning, removing loose furniture, preparing the area, and some basic maintenance can often be handled by a
                homeowner. Simple cosmetic work may also be suitable for someone with the right tools and experience.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">What About Structural Work?</h3>
              <p className="mb-6">
                Replacing or modifying joists, beams, posts, footings, or connections is different. These components affect the
                stability of the deck and should be assessed carefully before work begins.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Consider Calgary Requirements</h3>
              <p className="mb-6">
                If your project involves structural changes, an elevated deck, stairs, an expansion, or other significant
                modifications, check current Calgary permit requirements before starting. Decking products can also have specific
                installation requirements that need to be followed.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">When Should You Hire a Professional?</h3>
              <p className="mb-4">
                A professional assessment is worthwhile when you are unsure about the condition of the framing, want to change the
                deck's size or layout, or have concerns about movement, rot, footings, or connections.
              </p>
              <p className="mb-6">
                For a substantial remodel, getting the structure assessed first can help you avoid spending money on new materials
                before knowing whether the existing deck can support the planned changes.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Calgary Specific Considerations for Deck Remodeling</h2>
              <p className="mb-6">
                A deck remodel in Calgary needs to account for conditions that are different from many other parts of Canada. The
                materials and construction approach should suit the local climate and how the deck will be used throughout the year.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Freeze and Thaw Conditions</h3>
              <p className="mb-6">
                Calgary can experience significant temperature changes between seasons and during winter. Repeated freezing and
                thawing can put stress on outdoor materials and make proper construction and drainage important.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Snow and Moisture</h3>
              <p className="mb-6">
                Snow, spring melt, and moisture can affect both decking and the structure underneath. During a remodel, areas that
                hold water or show signs of moisture damage should be addressed before new decking is installed.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Material Selection</h3>
              <p className="mb-6">
                Choose decking based on appearance, maintenance, budget, and expected performance in Calgary's climate. Wood and
                composite can both work well when the product and installation method are appropriate for the project.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Drainage</h3>
              <p className="mb-6">
                Water should have a clear path away from the deck and its supporting structure. Poor drainage can contribute to
                moisture problems over time, particularly around framing and areas connected to the house.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Long Term Maintenance</h3>
              <p className="mb-4">
                Every decking material has maintenance requirements. A good remodel should consider not only how the deck looks when
                completed, but also how much upkeep the homeowner is comfortable doing over the years.
              </p>
              <p className="mb-6">
                For Calgary homeowners, the goal is not simply to make an older deck look new. The remodel should leave the
                structure, materials, drainage, and maintenance plan suitable for the local conditions.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Frequently Asked Questions</h2>
              
              <div className="space-y-4 my-8">
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0 pr-4">Can You Remodel an Existing Deck?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 flex-shrink-0">▼</span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed font-text">
                    Yes, an existing deck can often be remodeled when the underlying structure is still in suitable condition. You may
                    be able to replace the decking, railings, stairs, or other components without rebuilding the entire deck. The
                    framing, posts, beams, connections, and footings should be assessed before deciding what can be retained.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0 pr-4">How Do I Know If My Deck Frame Is Still Good?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 flex-shrink-0">▼</span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed font-text">
                    Look for signs such as rot, soft wood, sagging, movement, splitting, damaged connections, or deterioration around
                    posts, beams, and the ledger area. A visual check can identify obvious problems, but structural concerns should be
                    assessed by someone qualified before remodeling work begins.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0 pr-4">Can I Put Composite Decking Over an Existing Deck?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 flex-shrink-0">▼</span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed font-text">
                    Not automatically. The existing framing must be suitable for the specific composite product, including its joist
                    spacing, fastening requirements, ventilation, and installation pattern. The manufacturer's installation instructions
                    should be followed before installing composite decking over an existing structure.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0 pr-4">How Much Does It Cost to Remodel a Deck in Calgary?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 flex-shrink-0">▼</span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed font-text">
                    There is no single price because the cost depends on the size and condition of the deck, materials, structural
                    repairs, railings, stairs, access, and overall scope. Calgary market benchmarks can provide a starting point, but an
                    assessment is needed for a project specific estimate.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0 pr-4">Do I Need a Permit to Remodel My Deck in Calgary?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 flex-shrink-0">▼</span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed font-text">
                    It depends on the deck and the work being completed. An elevated uncovered deck over 0.6 metres above grade can
                    require a building permit, including certain structural work, extensions, and stair work. Always check the current
                    City of Calgary requirements before starting the project.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0 pr-4">Is It Cheaper to Remodel or Replace a Deck?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 flex-shrink-0">▼</span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed font-text">
                    Remodeling can cost less when the existing structure is sound and only selected components need to be replaced. If
                    the framing or foundation has widespread deterioration, repeated repairs may not be practical and a complete
                    replacement can make more sense.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0 pr-4">How Long Does a Deck Remodel Take?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 flex-shrink-0">▼</span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed font-text">
                    The timeline depends on the size of the deck, its existing condition, the amount of structural work, materials,
                    design changes, and permitting requirements. A straightforward resurfacing project will generally take less time
                    than a remodel involving structural repairs or a larger layout change.
                  </div>
                </details>
              </div>

              {/* Call to Action Section */}
              <div className="mt-16 mb-8 border-t border-gray-200 pt-12 bg-gray-900 rounded-xl p-8 shadow-xl border-l-4 border-[#86efac]">
                <h2 className="text-2xl font-bold mb-4 font-secondary text-[#86efac]">Ready to Remodel Your Calgary Deck?</h2>
                <p className="mb-4 text-gray-300">
                  If your existing deck is showing its age, the first step is to understand what can be retained and what needs to be
                  replaced. Busy Beaver can assess the existing structure and help you determine whether resurfacing, remodeling, or a
                  complete replacement makes the most sense for your Calgary home.
                </p>
                <p className="mb-4 text-gray-300">
                  With 25 years in business, Busy Beaver brings long-standing local experience to deck projects in Calgary. The
                  company is also bonded, licensed and insured, holds an A+ BBB rating, and is a TimberTech Platinum builder.
                </p>
                <p className="mb-6 text-gray-300">
                  Whether you are replacing worn decking, upgrading to composite, improving the railings, or changing the layout, the
                  goal is to create a deck that fits your home and how you use your outdoor space.
                </p>
                <p className="text-gray-300 mb-6">
                  Ready to explore your options?
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="https://busybeaverconstruction.com/estimate?utm_source=chatgpt.com" 
                    onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/estimate?utm_source=chatgpt.com')}
                    className="inline-flex justify-center items-center px-6 py-3 bg-[#86efac] text-black font-bold rounded shadow-lg hover:brightness-110 transition-all duration-300 font-accent tracking-wide"
                  >
                    Get a deck estimate
                  </a>
                  <a 
                    href="https://busybeaverconstruction.com/contact" 
                    onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/contact')}
                    className="inline-flex justify-center items-center px-6 py-3 bg-white text-black font-bold rounded shadow-lg hover:bg-gray-100 transition-all duration-300 font-accent tracking-wide"
                  >
                    contact Busy Beaver
                  </a>
                </div>
                <p className="text-gray-400 mt-4 text-sm italic">
                  to discuss your project.
                </p>
              </div>

            </article>

            {/* Sidebar Inspiration Gallery */}
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit mt-8 lg:mt-6">
              <div className="bg-white rounded shadow-sm border border-gray-100 pb-6">
                <h3 className="text-[22px] font-bold mb-4 font-secondary text-[#0f172a] px-6 pt-6 tracking-tight">Project Inspiration</h3>
                
                <div className="grid grid-cols-1 gap-5 max-h-[850px] overflow-y-auto px-6 custom-scrollbar">
                  {[
                    "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
                    "/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-deck-with-PT-wood-skirting-for-underdeck-storage.jpg",
                    "/assets/images/Decks/Wood Decks/Multi-level-Cedar-wood-deck.jpg"
                  ].map((src, index) => (
                    <div key={index} className="overflow-hidden h-[220px] group cursor-pointer shadow-sm border-2 border-white ring-1 ring-gray-100">
                      <img 
                        src={src}
                        alt={`Inspiration image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 loading-lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            
          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}